/**
 * src/offline/captureQueue.web.ts — WEB capture queue, IndexedDB-backed (web-companion spec W4 / §2.6).
 *
 * Web counterpart of the native expo-sqlite queue (captureQueue.ts). Same public surface + the same
 * client-UUID idempotency key, so capture / review / connectivity code runs unchanged in the browser.
 * Storage is IndexedDB, so a capture queued while offline survives a reload and replays on reconnect.
 * Web has weaker background execution than native, so web offline remains best-effort (the tab must be
 * opened to replay) — mobile stays the primary field-capture device.
 *
 * The pure helpers + types MIRROR captureQueue.ts exactly. If you change them there, change them here.
 * NEVER imports @smartbox/* (mobile invariant 1). No PII is logged.
 */
import { uuidV4 } from './uuid';
import { ingest } from '../api/smartbox';
import { useQueueStore } from '../review/queueStore';
import type { IngestFile } from '@beam/api-types';

export const MAX_ATTEMPTS = 5;

export type CaptureStatus = 'pending' | 'syncing' | 'synced' | 'failed';

export interface CaptureQueueRow {
  id: string;
  filename: string;
  mime_type: string;
  content_base64: string;
  created_at: string;
  status: CaptureStatus;
  attempts: number;
  last_error: string | null;
  batch_id: string | null;
  synced_at: string | null;
}

export function isEligibleForSync(row: Pick<CaptureQueueRow, 'status' | 'attempts'>): boolean {
  return (row.status === 'pending' || row.status === 'failed') && row.attempts < MAX_ATTEMPTS;
}
export function isBeyondRetryLimit(row: Pick<CaptureQueueRow, 'attempts'>): boolean {
  return row.attempts >= MAX_ATTEMPTS;
}

export interface SyncedUpdate { status: 'synced'; batch_id: string; synced_at: string }
export function buildSyncedUpdate(batchId: string): SyncedUpdate {
  return { status: 'synced', batch_id: batchId, synced_at: new Date().toISOString() };
}
export interface FailedUpdate { status: 'failed'; attempts: number; last_error: string }
export function buildFailedUpdate(currentAttempts: number, errorMessage: string): FailedUpdate {
  return { status: 'failed', attempts: currentAttempts + 1, last_error: errorMessage.slice(0, 500) };
}

export interface EnqueueInput { filename: string; mimeType: string; contentBase64: string }

// ---------------------------------------------------------------------------
// IndexedDB layer (one object store keyed by the client UUID = idempotency key)
// ---------------------------------------------------------------------------

const DB_NAME = 'beam_capture_queue';
const STORE = 'capture_queue';
const DB_VERSION = 1;

let _dbPromise: Promise<IDBDatabase> | null = null;

function hasIndexedDb(): boolean {
  return typeof indexedDB !== 'undefined';
}

function openDb(): Promise<IDBDatabase> {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return _dbPromise;
}

function reqToPromise<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function getAllRows(): Promise<CaptureQueueRow[]> {
  if (!hasIndexedDb()) return [];
  const db = await openDb();
  const tx = db.transaction(STORE, 'readonly');
  const rows = await reqToPromise(tx.objectStore(STORE).getAll() as IDBRequest<CaptureQueueRow[]>);
  return rows ?? [];
}

async function getRow(id: string): Promise<CaptureQueueRow | undefined> {
  if (!hasIndexedDb()) return undefined;
  const db = await openDb();
  const tx = db.transaction(STORE, 'readonly');
  return reqToPromise(tx.objectStore(STORE).get(id) as IDBRequest<CaptureQueueRow | undefined>);
}

async function putRow(row: CaptureQueueRow): Promise<void> {
  if (!hasIndexedDb()) return;
  const db = await openDb();
  const tx = db.transaction(STORE, 'readwrite');
  tx.objectStore(STORE).put(row);
  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

async function deleteRow(id: string): Promise<void> {
  if (!hasIndexedDb()) return;
  const db = await openDb();
  const tx = db.transaction(STORE, 'readwrite');
  tx.objectStore(STORE).delete(id);
  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// ---------------------------------------------------------------------------
// Public surface (mirrors captureQueue.ts)
// ---------------------------------------------------------------------------

export async function enqueueCapture(input: EnqueueInput): Promise<string> {
  const id = uuidV4();
  await putRow({
    id,
    filename: input.filename,
    mime_type: input.mimeType,
    content_base64: input.contentBase64,
    created_at: new Date().toISOString(),
    status: 'pending',
    attempts: 0,
    last_error: null,
    batch_id: null,
    synced_at: null,
  });
  return id;
}

export async function listQueue(status?: CaptureStatus): Promise<CaptureQueueRow[]> {
  const all = (await getAllRows()).sort((a, b) => a.created_at.localeCompare(b.created_at));
  return status === undefined ? all : all.filter((r) => r.status === status);
}

export async function getQueueCounts(): Promise<{ pending: number; failed: number; synced: number; syncing: number }> {
  const out = { pending: 0, failed: 0, synced: 0, syncing: 0 };
  for (const r of await getAllRows()) out[r.status] += 1;
  return out;
}

export async function syncQueue(): Promise<{ synced: number; failed: number }> {
  let synced = 0;
  let failed = 0;
  const rows = (await getAllRows())
    .filter((r) => isEligibleForSync(r))
    .sort((a, b) => a.created_at.localeCompare(b.created_at));

  for (const row of rows) {
    // Re-read inside the loop to guard against a concurrent sync (idempotency by status).
    const fresh = await getRow(row.id);
    if (!fresh || !isEligibleForSync(fresh)) continue;

    await putRow({ ...fresh, status: 'syncing' });
    try {
      const file: IngestFile = { filename: fresh.filename, mimeType: fresh.mime_type, contentBase64: fresh.content_base64 };
      const batchResult = await ingest([file]);
      const update = buildSyncedUpdate(batchResult.batchId);
      await putRow({ ...fresh, status: update.status, batch_id: update.batch_id, synced_at: update.synced_at });
      useQueueStore.getState().addBatch(batchResult);
      synced++;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      const update = buildFailedUpdate(fresh.attempts, msg);
      await putRow({ ...fresh, status: update.status, attempts: update.attempts, last_error: update.last_error });
      failed++;
    }
  }
  return { synced, failed };
}

export async function pruneSynced(olderThanMs: number = 7 * 24 * 60 * 60 * 1000): Promise<number> {
  const cutoff = new Date(Date.now() - olderThanMs).toISOString();
  let removed = 0;
  for (const r of await getAllRows()) {
    if (r.status === 'synced' && r.synced_at && r.synced_at < cutoff) {
      await deleteRow(r.id);
      removed++;
    }
  }
  return removed;
}
